import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import PageLayout from "examples/LayoutContainers/PageLayout";
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import Footer from "layouts/authentication/components/Footer";
import SkipToContent from "components/SkipToContent";
import usePageTitle from "utils/usePageTitle";
import pageRoutes from "page.routes";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Rating from "@mui/material/Rating";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";

import { primaryBtnSx } from "../formular-sesizare/colors";
import useAPI from "utils/useApi";
import TurnstileWidget, { TURNSTILE_RESPONSE_FIELD } from "components/TurnstileWidget";


const PALETTE = {
  navy: "#061D36",
  title: "#0f2c4a",
  body: "#5a6b85",
  muted: "#8b9cb0",
  border: "rgba(6, 29, 54, 0.08)",
  surface: "#f8f9fb",
  blueAccent: "#1565C0",
};

const RATING_SUBTITLE = "1 = foarte slab, 5 = foarte bine";
const CHECKBOX_SUBTITLE = "Puteți selecta mai multe opțiuni";

const selectSx = {
  height: "45px",
  color: PALETTE.title,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: PALETTE.border,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: PALETTE.blueAccent,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: PALETTE.blueAccent,
  },
};

function buildInitialAnswers(questions = []) {
  return questions.reduce((acc, question) => {
    if (question.type === "rating") {
      acc[question.id] = 0;
    } else if (question.type === "checkbox") {
      acc[question.id] = [];
    } else {
      acc[question.id] = "";
    }
    return acc;
  }, {});
}

function buildSubmitPayload(questions, answers) {
  return {
    answers: questions.map((question) => ({
      question_id: question.id,
      answer: answers[question.id],
    })),
  };
}

function getOptionLabel(option) {
  if (typeof option === "string") {
    return option;
  }

  const directFields = [
    "text",
    "label",
    "name",
    "title",
    "option_text",
    "option",
    "content",
    "answer",
    "description",
    "value",
  ];

  for (const field of directFields) {
    const value = option[field];
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }

  if (option.option && typeof option.option === "object") {
    return getOptionLabel(option.option);
  }

  const stringEntry = Object.entries(option).find(
    ([key, value]) => key !== "id" && typeof value === "string" && value.trim()
  );

  return stringEntry ? stringEntry[1] : "";
}

function getOptionValue(option, index) {
  return typeof option === "string" ? option : String(option.id ?? index);
}

function getQuestionSubtitle(type) {
  switch (type) {
    case "rating":
      return RATING_SUBTITLE;
    case "text":
      return "Opțional";
    case "checkbox":
      return CHECKBOX_SUBTITLE;
    default:
      return undefined;
  }
}


function QuestionBlock({ number, title, subtitle, children }) {
  return (
    <SoftBox
      mb={3}
      p={2.5}
      borderRadius="lg"
      sx={{
        bgcolor: PALETTE.surface,
        border: `1px solid ${PALETTE.border}`,
      }}
    >
      <SoftBox display="flex" alignItems="flex-start" gap={1.5} mb={2}>
        <SoftBox
          sx={{
            minWidth: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: PALETTE.blueAccent,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.8rem",
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {number}
        </SoftBox>
        <SoftBox flex={1}>
          <SoftTypography variant="h6" fontWeight="bold" sx={{ color: PALETTE.title, fontSize: "1rem" }}>
            {title}
          </SoftTypography>
          {subtitle && (
            <SoftTypography variant="caption" sx={{ color: PALETTE.muted, display: "block", mt: 0.25 }}>
              {subtitle}
            </SoftTypography>
          )}
        </SoftBox>
      </SoftBox>
      {children}
    </SoftBox>
  );
}

function QuestionField({ question, value, onChange, ratingSx }) {
  switch (question.type) {
    case "radio":
      return (
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            name={`question-${question.id}`}
            value={value}
            onChange={(event) => onChange(question.id, event.target.value)}
          >
            {question.options.map((option, index) => {
              const optionValue = getOptionValue(option, index);
              const label = getOptionLabel(option);

              return (
                <FormControlLabel
                  key={option.id ?? `${question.id}-${index}`}
                  value={optionValue}
                  control={
                    <Radio
                      sx={{
                        color: PALETTE.muted,
                        "&.Mui-checked": { color: PALETTE.blueAccent },
                      }}
                    />
                  }
                  label={label}
                />
              );
            })}
          </RadioGroup>
        </FormControl>
      );

    case "select":
      return (
        <FormControl fullWidth>
          <Select
            value={value || ""}
            displayEmpty
            onChange={(event) => onChange(question.id, event.target.value)}
            sx={selectSx}
          >
            <MenuItem value="" disabled>
              Selectați o opțiune
            </MenuItem>
            {question.options.map((option, index) => {
              const optionValue = getOptionValue(option, index);
              const label = getOptionLabel(option);

              return (
                <MenuItem key={option.id ?? `${question.id}-${index}`} value={optionValue}>
                  {label}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      );

    case "checkbox": {
      const selected = Array.isArray(value) ? value : [];

      return (
        <FormControl component="fieldset" fullWidth>
          {question.options.map((option, index) => {
            const optionValue = getOptionValue(option, index);
            const label = getOptionLabel(option);
            const checked = selected.includes(optionValue);

            return (
              <FormControlLabel
                key={option.id ?? `${question.id}-${index}`}
                control={
                  <Checkbox
                    checked={checked}
                    onChange={(event) => {
                      const next = event.target.checked
                        ? [...selected, optionValue]
                        : selected.filter((item) => item !== optionValue);
                      onChange(question.id, next);
                    }}
                    sx={{
                      color: PALETTE.muted,
                      "&.Mui-checked": { color: PALETTE.blueAccent },
                    }}
                  />
                }
                label={label}
              />
            );
          })}
        </FormControl>
      );
    }

    case "rating":
      return (
        <Rating
          name={`question-${question.id}`}
          value={Number(value) || 0}
          max={5}
          onChange={(_, newValue) => onChange(question.id, newValue || 0)}
          size="large"
          sx={ratingSx}
        />
      );

    case "text":
      return (
        <SoftInput
          multiline
          rows={4}
          placeholder="Scrieți răspunsul aici..."
          value={value}
          onChange={(event) => onChange(question.id, event.target.value)}
          fullWidth
        />
      );

    default:
      return null;
  }
}



function FeedbackFormPage() {
  const { token } = useParams();
  const [formData, setFormData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const client = useAPI();
  const turnstileSiteKey = formData?.turnstile_site_key;

  usePageTitle(formData?.title || "Formular feedback");

  const sortedQuestions = useMemo(
    () => [...(formData?.questions || [])].sort((a, b) => a.order - b.order),
    [formData]
  );

  useEffect(() => {
    if (!token) {
      setLoadError("Link-ul formularului nu este valid.");
      setLoading(false);
      return;
    }

    const fetchFeedbackForm = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const response = await client.getFeedbackForm(token);
        setFormData(response);
        setAnswers(buildInitialAnswers(response.questions));
      } catch (err) {
        console.error("Error fetching feedback form:", err);
        setLoadError("Formularul nu a putut fi încărcat. Verificați link-ul sau încercați din nou.");
      } finally {
        setLoading(false);
      }
    };

    fetchFeedbackForm();
  }, [token]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token || submitting || !formData) return;

    if (turnstileSiteKey && !turnstileToken) {
      setSubmitError("Vă rugăm să completați verificarea de securitate înainte de trimitere.");
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);
      const payload = buildSubmitPayload(sortedQuestions, answers);
      if (turnstileToken) {
        payload[TURNSTILE_RESPONSE_FIELD] = turnstileToken;
      }
      await client.registerFeedbackForm(token, payload);
      setSubmitted(true);
    } catch (err) {
      console.error("Error submitting feedback form:", err);
      setSubmitError("Feedback-ul nu a putut fi trimis. Încercați din nou.");
      setTurnstileToken(null);
      setTurnstileResetKey((key) => key + 1);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTurnstileExpire = () => {
    setTurnstileToken(null);
    setTurnstileResetKey((key) => key + 1);
  };

  const ratingSx = {
    color: PALETTE.blueAccent,
    "& .MuiRating-iconFilled": { color: "#FFC107" },
    "& .MuiRating-iconHover": { color: "#FFB300" },
  };

  return (
    <PageLayout>
      <SkipToContent />
      <DefaultNavbar
        routes={pageRoutes}
        transparent
        action={{
          type: "external",
          route: "/formular-sesizare",
          label: "Trimite Sesizare",
          color: "info",
        }}
      />

      <SoftBox component="main" id="main-content" tabIndex={-1} sx={{ outline: "none" }} mt={18} mb={5}>
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={10} md={8} lg={7}>
            <Card sx={{ overflow: "visible", boxShadow: "0 8px 32px rgba(6, 29, 54, 0.08)" }}>
              <SoftBox p={{ xs: 3, md: 4 }}>
                <SoftBox textAlign="center" mb={4}>
                  <SoftBox
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      bgcolor: "rgba(21, 101, 192, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                    }}
                  >
                    <Icon sx={{ color: PALETTE.blueAccent, fontSize: "1.75rem !important" }}>rate_review</Icon>
                  </SoftBox>
                  <SoftTypography variant="h3" fontWeight="bold" sx={{ color: PALETTE.title, fontSize: { xs: "1.5rem", md: "1.75rem" } }}>
                    {formData?.title || "Feedback platformă"}
                  </SoftTypography>
                  {formData?.description ? (
                    <SoftTypography variant="body2" sx={{ color: PALETTE.body, mt: 1, maxWidth: 480, mx: "auto" }}>
                      {formData.description}
                    </SoftTypography>
                  ) : (
                    !loading && !loadError && (
                      <SoftTypography variant="body2" sx={{ color: PALETTE.body, mt: 1, maxWidth: 480, mx: "auto" }}>
                        Ne ajută să îmbunătățim experiența pe stoprasism.ro. Răspunsurile sunt anonime.
                      </SoftTypography>
                    )
                  )}
                </SoftBox>

                {loading ? (
                  <SoftBox textAlign="center" py={5}>
                    <SoftTypography variant="body2" sx={{ color: PALETTE.body }}>
                      Se încarcă formularul...
                    </SoftTypography>
                  </SoftBox>
                ) : loadError ? (
                  <SoftBox
                    textAlign="center"
                    py={5}
                    px={3}
                    borderRadius="lg"
                    sx={{
                      bgcolor: "rgba(211, 47, 47, 0.06)",
                      border: "1px solid rgba(211, 47, 47, 0.2)",
                    }}
                  >
                    <Icon sx={{ color: "#d32f2f", fontSize: "3rem !important", mb: 2 }}>error_outline</Icon>
                    <SoftTypography variant="body2" sx={{ color: PALETTE.body }}>
                      {loadError}
                    </SoftTypography>
                  </SoftBox>
                ) : submitted ? (
                  <SoftBox
                    textAlign="center"
                    py={5}
                    px={3}
                    borderRadius="lg"
                    sx={{
                      bgcolor: "rgba(46, 125, 50, 0.06)",
                      border: "1px solid rgba(46, 125, 50, 0.2)",
                    }}
                  >
                    <Icon sx={{ color: "#2e7d32", fontSize: "3rem !important", mb: 2 }}>check_circle</Icon>
                    <SoftTypography variant="h5" fontWeight="bold" sx={{ color: PALETTE.title, mb: 1 }}>
                      Mulțumim pentru feedback!
                    </SoftTypography>
                    <SoftTypography variant="body2" sx={{ color: PALETTE.body }}>
                      Răspunsurile dumneavoastră ne ajută să îmbunătățim platforma.
                    </SoftTypography>
                  </SoftBox>
                ) : (
                  <SoftBox component="form" onSubmit={handleSubmit}>
                    {sortedQuestions.map((question, index) => (
                      <QuestionBlock
                        key={question.id}
                        number={index + 1}
                        title={question.question}
                        subtitle={getQuestionSubtitle(question.type)}
                      >
                        <QuestionField
                          question={question}
                          value={answers[question.id]}
                          onChange={handleAnswerChange}
                          ratingSx={ratingSx}
                        />
                      </QuestionBlock>
                    ))}

                    <SoftBox textAlign="center" mt={4}>
                      {submitError && (
                        <SoftTypography variant="body2" sx={{ color: "#d32f2f", mb: 2 }}>
                          {submitError}
                        </SoftTypography>
                      )}
                      {turnstileSiteKey && (
                        <SoftBox mb={2} display="flex" justifyContent="center">
                          <TurnstileWidget
                            key={`turnstile-feedback-${turnstileResetKey}`}
                            siteKey={turnstileSiteKey}
                            onVerify={setTurnstileToken}
                            onExpire={handleTurnstileExpire}
                            onError={handleTurnstileExpire}
                          />
                        </SoftBox>
                      )}
                      <SoftButton
                        type="submit"
                        variant="contained"
                        disabled={submitting || (turnstileSiteKey && !turnstileToken)}
                        sx={{ ...primaryBtnSx, px: 5, py: 1.25 }}
                      >
                        {submitting ? "Se trimite..." : "Trimite feedback"}
                      </SoftButton>
                    </SoftBox>
                  </SoftBox>
                )}
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>

      <Footer />
    </PageLayout>
  );
}

export default FeedbackFormPage;
